import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  AttachmentKind,
  AttachmentListResponse,
  AttachmentUploadResponse,
} from '@chatsdet/types';
import { API_URL } from '../../config';

type AttachmentListQuery = {
  page?: number;
  pageSize?: number;
  search?: string;
  kind?: AttachmentKind;
};

const buildQueryString = (params: AttachmentListQuery): string => {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', String(params.page));
  if (params.pageSize) searchParams.set('pageSize', String(params.pageSize));
  if (params.search) searchParams.set('search', params.search);
  if (params.kind && params.kind !== 'all') searchParams.set('kind', params.kind);
  return searchParams.toString();
};

// Consumed by `<img>`/`<audio>` src and `window.open` — none can send the org
// header, so carry the active org as a query param.
export const attachmentRawUrl = (attachmentId: string) =>
  `${API_URL}/org/attachments/${attachmentId}/raw`;

const attachmentsKeys = {
  all: ['attachments'] as const,
  list: (params: AttachmentListQuery) =>
    [
      ...attachmentsKeys.all,
      'list',
      params.page ?? 1,
      params.pageSize ?? 24,
      params.search ?? '',
      params.kind ?? 'all',
    ] as const,
};

const fetchAttachmentList = async (
  params: AttachmentListQuery
): Promise<AttachmentListResponse> => {
  const query = buildQueryString(params);
  const response = await fetch(`${API_URL}/org/attachments${query ? `?${query}` : ''}`, {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Failed to load attachments: ${response.status}`);
  }
  const body = (await response.json()) as AttachmentListResponse;
  if (!body || !body.ok) {
    throw new Error('Failed to load attachments');
  }
  return body;
};

const resolveAttachments = async (ids: string[]): Promise<AttachmentUploadResponse> => {
  const response = await fetch(`${API_URL}/org/attachments/resolve`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ids }),
  });
  if (!response.ok) {
    throw new Error(`Failed to resolve attachments: ${response.status}`);
  }
  const body = (await response.json()) as AttachmentUploadResponse;
  if (!body || !body.ok) {
    throw new Error('Failed to resolve attachments');
  }
  return body;
};

const uploadAttachments = async (files: File[]): Promise<AttachmentUploadResponse> => {
  const form = new FormData();
  for (const file of files) {
    form.append('files', file);
  }

  const response = await fetch(`${API_URL}/org/attachments`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `Failed to upload attachments: ${response.status}`);
  }
  const body = (await response.json()) as AttachmentUploadResponse;
  if (!body || !body.ok) {
    throw new Error('Failed to upload attachments');
  }
  return body;
};

const deleteAttachment = async (id: string): Promise<{ ok: true }> => {
  const response = await fetch(`${API_URL}/org/attachments/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Failed to delete attachment: ${response.status}`);
  }
  const body = (await response.json()) as { ok: true } | { ok: false; error?: string };
  if (!body || body.ok !== true) {
    throw new Error('Failed to delete attachment');
  }
  return body;
};

export const useAttachmentList = (
  params: AttachmentListQuery
): UseQueryResult<AttachmentListResponse> => {
  return useQuery<AttachmentListResponse>({
    queryKey: attachmentsKeys.list(params),
    queryFn: () => fetchAttachmentList(params),
    placeholderData: (previous) => previous,
    staleTime: 30_000,
  });
};

export const useResolvedAttachments = (ids: string[]): UseQueryResult<AttachmentUploadResponse> => {
  return useQuery<AttachmentUploadResponse>({
    queryKey: [...attachmentsKeys.all, 'resolve', ids.slice().sort().join(',')],
    queryFn: () => resolveAttachments(ids),
    enabled: ids.length > 0,
    placeholderData: (previous) => (ids.length === 0 ? undefined : previous),
    staleTime: 30_000,
  });
};

export const useUploadAttachments = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (files: File[]) => uploadAttachments(files),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: attachmentsKeys.all });
    },
  });
};

export const useDeleteAttachment = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteAttachment(id),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: attachmentsKeys.all });
    },
  });
};
